import React, { useState } from 'react';
import { useData } from '../context/DataContext';
import { useAuth } from '../context/AuthContext';
import { PageHeader, Tabs, Modal, StatusBadge, ProgressBar } from '../components/UI';

const TODAY = '2025-12-10';
const CHART_START = new Date('2025-01-01');
const CHART_END   = new Date('2026-10-01');
const TOTAL_DAYS  = (CHART_END - CHART_START) / 86400000;

function pctLeft(dateStr) { return Math.max(0, (new Date(dateStr) - CHART_START) / 86400000 / TOTAL_DAYS * 100); }
function pctWidth(s, e)   { return Math.max(0.4, (new Date(e) - new Date(s)) / 86400000 / TOTAL_DAYS * 100); }

const MONTHS = [];
let d = new Date(CHART_START);
while (d < CHART_END) { MONTHS.push(new Date(d)); d = new Date(d.getFullYear(), d.getMonth() + 1, 1); }

const EMPTY_TASK = { code: '', name: '', parent: null, progress: 0, start: '', end: '', duration: 0, color: '#3b82f6' };

export default function Schedule() {
  const { data, activeProject, upsert, deleteItem } = useData();
  const { can } = useAuth();
  const [tab, setTab] = useState('gantt');
  const [form, setForm] = useState(null);

  const items = data.wbs.filter(w => w.projectId === activeProject?.id);
  const topLevel = items.filter(i => !i.parent);
  const children = pid => items.filter(i => i.parent === pid);
  const todayPct = pctLeft(TODAY);

  const save = () => {
    upsert('wbs', { ...form, projectId: activeProject.id });
    setForm(null);
  };

  const handleImport = () => alert('MS Project XML import: export your .mpp as XML from MS Project (File → Save As → XML format), then use the JSON import on the Settings page after converting via the provided utility.');

  return (
    <div className="page-content">
      <PageHeader title="Schedule / Gantt" subtitle={`${items.length} WBS items · ${activeProject?.name}`}>
        <button className="btn btn-ghost" onClick={handleImport}>📥 Import MS Project XML</button>
        {can('canCreate') && (
          <button className="btn btn-primary" onClick={() => setForm({ ...EMPTY_TASK, id: null, projectId: activeProject.id })}>+ Add Task</button>
        )}
      </PageHeader>

      <Tabs
        tabs={[{ id: 'gantt', label: 'Gantt Chart' }, { id: 'wbs', label: 'WBS Table' }]}
        active={tab} onChange={setTab}
      />

      {tab === 'gantt' && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ display: 'flex' }}>
            {/* Labels column */}
            <div style={{ width: 290, minWidth: 290, borderRight: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
              <div style={{ padding: '0 12px', height: 28, background: 'var(--bg-elevated)', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Task Name
              </div>
              {topLevel.map(item => (
                <React.Fragment key={item.id}>
                  <div className="gantt-row gantt-label" style={{ fontWeight: 600, color: 'var(--text-primary)', cursor: 'pointer' }}
                    onClick={() => can('canEdit') && setForm({ ...item })}>
                    <span className="font-mono text-blue" style={{ fontSize: 11, minWidth: 34 }}>{item.code}</span>
                    <span className="truncate">{item.name}</span>
                    <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-muted)', minWidth: 30 }}>{item.progress}%</span>
                  </div>
                  {children(item.id).map(c => (
                    <div key={c.id} className="gantt-row gantt-label" style={{ paddingLeft: 28, cursor: 'pointer', color: 'var(--text-secondary)' }}
                      onClick={() => can('canEdit') && setForm({ ...c })}>
                      <span className="font-mono" style={{ fontSize: 11, minWidth: 34, color: 'var(--text-muted)' }}>{c.code}</span>
                      <span className="truncate" style={{ fontSize: 13 }}>{c.name}</span>
                      <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-muted)', minWidth: 30 }}>{c.progress}%</span>
                    </div>
                  ))}
                </React.Fragment>
              ))}
            </div>

            {/* Chart area */}
            <div style={{ flex: 1, overflowX: 'auto', position: 'relative' }}>
              <div style={{ minWidth: 900 }}>
                {/* Month headers */}
                <div className="gantt-header-row" style={{ position: 'relative' }}>
                  {MONTHS.map((m, i) => (
                    <div key={i} style={{ position: 'absolute', left: pctLeft(m.toISOString().split('T')[0]) + '%', top: 0, height: '100%', borderLeft: '1px solid var(--border)', paddingLeft: 4, fontSize: 10, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                      {m.toLocaleDateString('en', { month: 'short' })} {m.getFullYear()}
                    </div>
                  ))}
                </div>

                {/* Today line */}
                <div className="gantt-today-line" style={{ left: todayPct + '%', top: 28 }} />

                {topLevel.map(item => (
                  <React.Fragment key={item.id}>
                    <div className="gantt-row">
                      <div className="gantt-chart-area">
                        <div className="gantt-bar" style={{ left: pctLeft(item.start) + '%', width: pctWidth(item.start, item.end) + '%', background: item.color + 'cc', border: '1px solid ' + item.color }}
                          title={`${item.name}: ${item.start} → ${item.end} (${item.progress}%)`}>
                          <div className="gantt-progress-fill" style={{ width: item.progress + '%', background: item.color }} />
                          <span style={{ position: 'relative', zIndex: 1 }}>{item.name}</span>
                        </div>
                      </div>
                    </div>
                    {children(item.id).map(c => (
                      <div key={c.id} className="gantt-row" style={{ height: 30 }}>
                        <div className="gantt-chart-area" style={{ height: 30 }}>
                          <div className="gantt-bar" style={{ top: 5, height: 16, left: pctLeft(c.start) + '%', width: pctWidth(c.start, c.end) + '%', background: c.color + '99', border: '1px solid ' + c.color + '66', fontSize: 10 }}
                            title={`${c.name}: ${c.start} → ${c.end} (${c.progress}%)`}>
                            <div className="gantt-progress-fill" style={{ width: c.progress + '%', background: c.color }} />
                            <span style={{ position: 'relative', zIndex: 1 }}>{c.name}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'wbs' && (
        <div className="table-wrap">
          <table>
            <thead><tr>
              <th>Code</th><th>Task Name</th><th>Start</th><th>End</th><th>Duration</th><th>Progress</th><th>Color</th>
              {can('canEdit') && <th>Actions</th>}
            </tr></thead>
            <tbody>
              {items.map(i => (
                <tr key={i.id} className="clickable" onClick={() => can('canEdit') && setForm({ ...i })}>
                  <td className="td-mono">{i.code}</td>
                  <td className="td-primary" style={{ paddingLeft: i.parent ? 28 : 14 }}>{i.name}</td>
                  <td>{i.start}</td><td>{i.end}</td>
                  <td>{i.duration}d</td>
                  <td style={{ minWidth: 140 }}><ProgressBar value={i.progress} /></td>
                  <td><div style={{ width: 14, height: 14, borderRadius: 3, background: i.color, border: '1px solid var(--border)' }} /></td>
                  {can('canEdit') && (
                    <td onClick={e => e.stopPropagation()}>
                      <button className="btn btn-danger btn-xs" onClick={() => deleteItem('wbs', i.id)}>Delete</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {form && (
        <Modal title={form.id ? 'Edit Task' : 'New Task'} onClose={() => setForm(null)}>
          <div className="form-grid">
            <div className="form-group"><label>WBS Code</label><input value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} /></div>
            <div className="form-group"><label>Task Name</label><input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
            <div className="form-group"><label>Parent Task ID (optional)</label><input value={form.parent || ''} onChange={e => setForm({ ...form, parent: e.target.value || null })} placeholder="e.g. w2" /></div>
            <div className="form-group"><label>Progress (%)</label><input type="number" min="0" max="100" value={form.progress} onChange={e => setForm({ ...form, progress: +e.target.value })} /></div>
            <div className="form-group"><label>Start Date</label><input type="date" value={form.start} onChange={e => setForm({ ...form, start: e.target.value })} /></div>
            <div className="form-group"><label>End Date</label><input type="date" value={form.end} onChange={e => {
              const dur = Math.round((new Date(e.target.value) - new Date(form.start)) / 86400000);
              setForm({ ...form, end: e.target.value, duration: dur > 0 ? dur : 0 });
            }} /></div>
            <div className="form-group"><label>Duration (days)</label><input type="number" value={form.duration} onChange={e => setForm({ ...form, duration: +e.target.value })} /></div>
            <div className="form-group"><label>Bar Color</label><input type="color" value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} /></div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-ghost" onClick={() => setForm(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={save}>Save Task</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
